import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { Slot } from '@radix-ui/react-slot';
import { X } from 'lucide-react';
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ComponentPropsWithoutRef,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes
} from 'react';
import { clsx } from 'clsx';
import { HelpTerm, useFieldHelp, useTermGlossary, useTermHelp } from './field-help-context';

export function cn(...values: Parameters<typeof clsx>): string {
  return clsx(...values);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'small' | 'medium' | 'icon';
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'medium', type = 'button', asChild = false, ...props },
  ref
) {
  const classes = cn('dd-button', `dd-button--${variant}`, `dd-button--${size}`, className);
  if (asChild) return <Slot ref={ref} className={classes} {...props} />;
  return <button ref={ref} type={type} className={classes} {...props} />;
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonProps>(function IconButton(
  { className, ...props },
  ref
) {
  return <Button ref={ref} className={cn('dd-icon-button', className)} size="icon" {...props} />;
});

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn('dd-input', className)} {...props} />;
  }
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cn('dd-input', 'dd-textarea', className)} {...props} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...props }, ref) {
    return <select ref={ref} className={cn('dd-input', 'dd-select', className)} {...props} />;
  }
);

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('dd-label', className)} {...props} />;
}

export function Field({
  label,
  htmlFor,
  error,
  hint,
  help,
  children,
  className
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  help?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const resolvedHelp = useFieldHelp(htmlFor, label);
  const describedBy = error ? `${htmlFor}-error` : hint ? `${htmlFor}-hint` : undefined;
  return (
    <div className={cn('dd-field', className)}>
      <div className="dd-field-label-row">
        <Label htmlFor={htmlFor}>{label}</Label>
        {help ?? resolvedHelp}
      </div>
      <div aria-describedby={describedBy} aria-invalid={Boolean(error)}>
        {children}
      </div>
      {error ? (
        <p className="dd-field-error" id={`${htmlFor}-error`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="dd-field-hint" id={`${htmlFor}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function Card(
  { className, ...props },
  ref
) {
  return <div ref={ref} className={cn('dd-card', className)} {...props} />;
});

export function Badge({
  className,
  tone = 'neutral',
  ...props
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: 'neutral' | 'purple' | 'success' | 'warning' | 'danger';
}) {
  return <span className={cn('dd-badge', `dd-badge--${tone}`, className)} {...props} />;
}

export interface ModalProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'small' | 'medium' | 'large';
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'medium'
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dd-dialog-overlay" />
        <Dialog.Content className={cn('dd-dialog', `dd-dialog--${size}`)}>
          <div className="dd-dialog-header">
            <div>
              <Dialog.Title>{title}</Dialog.Title>
              {description ? <Dialog.Description>{description}</Dialog.Description> : null}
            </div>
            <Dialog.Close asChild>
              <IconButton variant="ghost" aria-label="Chiudi finestra">
                <X size={18} />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className="dd-dialog-body">{children}</div>
          {footer ? <div className="dd-dialog-footer">{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Conferma',
  onConfirm,
  busy = false
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  description: string;
  confirmLabel?: string;
  onConfirm(): void;
  busy?: boolean;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="small"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Annulla
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Operazione…' : confirmLabel}
          </Button>
        </>
      }
    >
      <p>Questa azione potrebbe non essere reversibile.</p>
    </Modal>
  );
}

export const Tabs = TabsPrimitive.Root;
export const TabsList = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(function TabsList({ className, ...props }, ref) {
  return <TabsPrimitive.List ref={ref} className={cn('dd-tabs-list', className)} {...props} />;
});
export const TabsTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & { helpTerm?: string }
>(function TabsTrigger({ className, helpTerm, ...props }, ref) {
  const trigger = (
    <TabsPrimitive.Trigger ref={ref} className={cn('dd-tabs-trigger', className)} {...props} />
  );
  return helpTerm ? (
    <span className="dd-tabs-trigger-with-help">
      {trigger}
      <span className="dd-tabs-trigger-help">
        <HelpTerm term={helpTerm}>
          <span className="dd-visually-hidden">{helpTerm}</span>
        </HelpTerm>
      </span>
    </span>
  ) : (
    trigger
  );
});
export const TabsContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(function TabsContent({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Content ref={ref} className={cn('dd-tabs-content', className)} {...props} />
  );
});

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;
export function MenuContent({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content className={cn('dd-menu', className)} sideOffset={6} {...props} />
    </DropdownMenu.Portal>
  );
}
export function MenuItem({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DropdownMenu.Item>) {
  return <DropdownMenu.Item className={cn('dd-menu-item', className)} {...props} />;
}
export const MenuSeparator = DropdownMenu.Separator;

export function PageHeader({
  eyebrow,
  title,
  description,
  actions
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  const titleHelp = useTermHelp(title);
  const glossary = useTermGlossary();
  return (
    <header className="dd-page-header">
      <div>
        {eyebrow ? <p className="dd-eyebrow">{eyebrow}</p> : null}
        <div className="dd-page-title-help">
          <h1>{title}</h1>
          {titleHelp}
        </div>
        {description ? <p>{description}</p> : null}
        {glossary.length ? (
          <div className="dd-page-glossary" aria-label="Glossario della schermata">
            <strong>Glossario:</strong>
            {glossary.map((term) => (
              <HelpTerm key={term} term={term} />
            ))}
          </div>
        ) : null}
      </div>
      {actions ? <div className="dd-page-actions">{actions}</div> : null}
    </header>
  );
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="dd-visually-hidden">{children}</span>;
}
